<!-- src/components/ui/DateTimePicker.vue -->
<template>
	<div class="datetime-picker" ref="container">
		<div
			class="datetime-picker__trigger"
			:class="{
				'datetime-picker__trigger--focused': isOpen,
				'datetime-picker__trigger--error': error,
				'datetime-picker__trigger--disabled': disabled,
			}"
			@click="togglePicker"
		>
			<div class="datetime-picker__icon">
				<svg
					v-if="mode !== 'time'"
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
				>
					<rect
						x="3"
						y="4"
						width="18"
						height="18"
						rx="2"
						stroke="currentColor"
						stroke-width="2"
					/>
					<line
						x1="3"
						y1="10"
						x2="21"
						y2="10"
						stroke="currentColor"
						stroke-width="2"
					/>
					<line
						x1="8"
						y1="2"
						x2="8"
						y2="6"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
					/>
					<line
						x1="16"
						y1="2"
						x2="16"
						y2="6"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
					/>
				</svg>
				<svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none">
					<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
					<path
						d="M12 7v5l3 3"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
					/>
				</svg>
			</div>
			<div class="datetime-picker__value">
				<span v-if="displayValue" class="datetime-picker__text">
					{{ displayValue }}
				</span>
				<span v-else class="datetime-picker__placeholder">
					{{ resolvedPlaceholder }}
				</span>
			</div>
			<div class="datetime-picker__arrow" :class="{ 'datetime-picker__arrow--open': isOpen }">
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
					<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
				</svg>
			</div>
		</div>

		<Teleport to="body">
			<Transition name="dropdown">
				<div
					v-if="isOpen"
					class="datetime-picker__dropdown"
					:style="dropdownStyle"
					ref="dropdown"
				>
					<div class="datetime-picker__content">
						<!-- Календарь -->
						<div v-if="isDateMode" class="datetime-picker__calendar">
							<div class="datetime-picker__calendar-header">
								<button
									type="button"
									class="datetime-picker__nav-btn"
									@click="prevMonth"
									:disabled="isPrevMonthDisabled"
								>
									<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
										<path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
									</svg>
								</button>
								<div class="datetime-picker__month-year">
									<button
										type="button"
										class="datetime-picker__month-btn"
										@click="showMonthSelector = !showMonthSelector"
									>
										{{ currentMonthName }} {{ currentYear }}
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none">
											<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
										</svg>
									</button>
								</div>
								<button
									type="button"
									class="datetime-picker__nav-btn"
									@click="nextMonth"
								>
									<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
										<path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
									</svg>
								</button>
							</div>

							<!-- Селектор месяца/года -->
							<Transition name="fade">
								<div v-if="showMonthSelector" class="datetime-picker__month-selector">
									<div class="datetime-picker__year-nav">
										<button type="button" @click="currentYear--" class="datetime-picker__year-btn">
											<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
												<path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
											</svg>
										</button>
										<span class="datetime-picker__year-label">{{ currentYear }}</span>
										<button type="button" @click="currentYear++" class="datetime-picker__year-btn">
											<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
												<path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
											</svg>
										</button>
									</div>
									<div class="datetime-picker__months-grid">
										<button
											v-for="(month, index) in monthNames"
											:key="index"
											type="button"
											class="datetime-picker__month-item"
											:class="{
												'datetime-picker__month-item--selected': currentMonth === index,
												'datetime-picker__month-item--disabled': isMonthDisabled(index),
											}"
											:disabled="isMonthDisabled(index)"
											@click="selectMonth(index)"
										>
											{{ month.short }}
										</button>
									</div>
								</div>
							</Transition>

							<!-- Дни недели -->
							<div class="datetime-picker__weekdays">
								<span v-for="day in weekDays" :key="day" class="datetime-picker__weekday">
									{{ day }}
								</span>
							</div>

							<!-- Дни месяца -->
							<div class="datetime-picker__days">
								<button
									v-for="day in calendarDays"
									:key="day.key"
									type="button"
									class="datetime-picker__day"
									:class="{
										'datetime-picker__day--other-month': !day.isCurrentMonth,
										'datetime-picker__day--today': day.isToday,
										'datetime-picker__day--selected': day.isSelected,
										'datetime-picker__day--disabled': day.isDisabled,
									}"
									:disabled="day.isDisabled"
									@click="selectDate(day)"
								>
									{{ day.day }}
								</button>
							</div>
						</div>

						<!-- Выбор времени -->
						<div v-if="isTimeMode" class="datetime-picker__time">
							<div class="datetime-picker__time-header">
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
									<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
									<path d="M12 7v5l3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
								</svg>
								<span>Время</span>
							</div>
							<div class="datetime-picker__time-selectors">
								<div class="datetime-picker__time-column">
									<button
										type="button"
										class="datetime-picker__time-btn datetime-picker__time-btn--up"
										@click="incrementHour"
									>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
											<path d="M18 15l-6-6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
										</svg>
									</button>
									<input
										type="text"
										class="datetime-picker__time-input"
										:value="hourInputValue"
										@input="handleHourInput"
										@focus="handleHourFocus"
										@blur="validateHour"
										@keydown="handleHourKeydown"
										maxlength="2"
										inputmode="numeric"
									/>
									<button
										type="button"
										class="datetime-picker__time-btn datetime-picker__time-btn--down"
										@click="decrementHour"
									>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
											<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
										</svg>
									</button>
								</div>
								<span class="datetime-picker__time-separator">:</span>
								<div class="datetime-picker__time-column">
									<button
										type="button"
										class="datetime-picker__time-btn datetime-picker__time-btn--up"
										@click="incrementMinute"
									>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
											<path d="M18 15l-6-6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
										</svg>
									</button>
									<input
										type="text"
										class="datetime-picker__time-input"
										:value="minuteInputValue"
										@input="handleMinuteInput"
										@focus="handleMinuteFocus"
										@blur="validateMinute"
										@keydown="handleMinuteKeydown"
										maxlength="2"
										inputmode="numeric"
									/>
									<button
										type="button"
										class="datetime-picker__time-btn datetime-picker__time-btn--down"
										@click="decrementMinute"
									>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
											<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
										</svg>
									</button>
								</div>
							</div>
							<!-- Быстрый выбор времени -->
							<div class="datetime-picker__quick-times">
								<button
									v-for="time in quickTimes"
									:key="time.value"
									type="button"
									class="datetime-picker__quick-time"
									:class="{ 'datetime-picker__quick-time--selected': isQuickTimeSelected(time) }"
									@click="selectQuickTime(time)"
								>
									{{ time.label }}
								</button>
							</div>
						</div>
					</div>

					<!-- Футер -->
					<div class="datetime-picker__footer">
						<button
							type="button"
							class="datetime-picker__footer-btn datetime-picker__footer-btn--clear"
							@click="clearSelection"
						>
							Очистить
						</button>
						<button
							type="button"
							class="datetime-picker__footer-btn datetime-picker__footer-btn--today"
							@click="selectToday"
						>
							Сегодня
						</button>
						<button
							type="button"
							class="datetime-picker__footer-btn datetime-picker__footer-btn--apply"
							@click="applySelection"
						>
							Применить
						</button>
					</div>
				</div>
			</Transition>
		</Teleport>
	</div>
</template>

<script>
export default {
	name: 'DateTimePicker',

	props: {
		modelValue: {
			type: String,
			default: '',
		},
		mode: {
			type: String,
			default: 'datetime',
			validator: value => ['datetime', 'date', 'time'].includes(value),
		},
		placeholder: {
			type: String,
			default: 'Выберите дату и время',
		},
		minDate: {
			type: [Date, String],
			default: null,
		},
		maxDate: {
			type: [Date, String],
			default: null,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		error: {
			type: Boolean,
			default: false,
		},
		minuteStep: {
			type: Number,
			default: 5,
		},
	},

	emits: ['update:modelValue', 'change'],

	data() {
		return {
			isOpen: false,
			showMonthSelector: false,
			currentMonth: new Date().getMonth(),
			currentYear: new Date().getFullYear(),
			selectedDate: null,
			selectedHour: 12,
			selectedMinute: 0,
			dropdownStyle: {},
			hourInputValue: '12',
			minuteInputValue: '00',
			isEditingHour: false,
			isEditingMinute: false,

			weekDays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
			monthNames: [
				{ full: 'Январь', short: 'Янв' },
				{ full: 'Февраль', short: 'Фев' },
				{ full: 'Март', short: 'Мар' },
				{ full: 'Апрель', short: 'Апр' },
				{ full: 'Май', short: 'Май' },
				{ full: 'Июнь', short: 'Июн' },
				{ full: 'Июль', short: 'Июл' },
				{ full: 'Август', short: 'Авг' },
				{ full: 'Сентябрь', short: 'Сен' },
				{ full: 'Октябрь', short: 'Окт' },
				{ full: 'Ноябрь', short: 'Ноя' },
				{ full: 'Декабрь', short: 'Дек' },
			],

			quickTimes: [
				{ label: '09:00', hour: 9, minute: 0 },
				{ label: '10:00', hour: 10, minute: 0 },
				{ label: '12:00', hour: 12, minute: 0 },
				{ label: '14:00', hour: 14, minute: 0 },
				{ label: '16:00', hour: 16, minute: 0 },
				{ label: '18:00', hour: 18, minute: 0 },
			],
		}
	},

	computed: {
		displayValue() {
			if (!this.modelValue) return ''

			try {
				if (this.mode === 'time') {
					const time = this.extractTime(this.modelValue)
					return time ? `${time.hours}:${time.minutes}` : ''
				}

				const date = this.extractDate(this.modelValue)
				if (!date) return ''

				const day = date.getDate()
				const month = this.monthNames[date.getMonth()].full.toLowerCase()
				const year = date.getFullYear()

				if (this.mode === 'date') {
					return `${day} ${month} ${year}`
				}

				const time = this.extractTime(this.modelValue) || {
					hours: this.selectedHour.toString().padStart(2, '0'),
					minutes: this.selectedMinute.toString().padStart(2, '0'),
				}

				return `${day} ${month} ${year}, ${time.hours}:${time.minutes}`
			} catch {
				return ''
			}
		},
		resolvedPlaceholder() {
			if (this.mode === 'date') {
				return 'Дата'
			}
			if (this.mode === 'time') {
				return 'Время'
			}
			return 'Выберите дату и время'
		},

		isDateMode() {
			return this.mode !== 'time'
		},

		isTimeMode() {
			return this.mode !== 'date'
		},

		currentMonthName() {
			return this.monthNames[this.currentMonth].full
		},

		minDateObj() {
			if (!this.minDate) return null
			if (this.minDate instanceof Date) return this.minDate
			return this.extractDate(String(this.minDate)) || new Date(this.minDate)
		},

		maxDateObj() {
			if (!this.maxDate) return null
			if (this.maxDate instanceof Date) return this.maxDate
			return this.extractDate(String(this.maxDate)) || new Date(this.maxDate)
		},

		isPrevMonthDisabled() {
			if (!this.minDateObj) return false
			const prevMonth = new Date(this.currentYear, this.currentMonth - 1, 1)
			const minMonth = new Date(this.minDateObj.getFullYear(), this.minDateObj.getMonth(), 1)
			return prevMonth < minMonth
		},

		calendarDays() {
			const days = []
			const firstDay = new Date(this.currentYear, this.currentMonth, 1)
			const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0)

			// Определяем день недели первого числа (0 = Вс, 1 = Пн, ...)
			let startDay = firstDay.getDay()
			// Конвертируем в формат Пн = 0
			startDay = startDay === 0 ? 6 : startDay - 1

			// Дни предыдущего месяца
			const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate()
			for (let i = startDay - 1; i >= 0; i--) {
				const day = prevMonthLastDay - i
				const date = new Date(this.currentYear, this.currentMonth - 1, day)
				days.push(this.createDayObject(date, false))
			}

			// Дни текущего месяца
			for (let day = 1; day <= lastDay.getDate(); day++) {
				const date = new Date(this.currentYear, this.currentMonth, day)
				days.push(this.createDayObject(date, true))
			}

			// Дни следующего месяца
			const remainingDays = 42 - days.length // 6 недель * 7 дней
			for (let day = 1; day <= remainingDays; day++) {
				const date = new Date(this.currentYear, this.currentMonth + 1, day)
				days.push(this.createDayObject(date, false))
			}

			return days
		},
	},

	watch: {
		modelValue: {
			immediate: true,
			handler(val) {
				if (val) {
					this.parseModelValue(val)
				}
			},
		},
		selectedHour(val) {
			if (!this.isEditingHour) {
				this.hourInputValue = val.toString().padStart(2, '0')
			}
		},
		selectedMinute(val) {
			if (!this.isEditingMinute) {
				this.minuteInputValue = val.toString().padStart(2, '0')
			}
		},
	},

	mounted() {
		document.addEventListener('click', this.handleClickOutside)
		document.addEventListener('keydown', this.handleKeydown)
	},

	beforeUnmount() {
		document.removeEventListener('click', this.handleClickOutside)
		document.removeEventListener('keydown', this.handleKeydown)
	},

	methods: {
		extractDate(value) {
			if (!value || typeof value !== 'string') return null
			const match = value.match(/(\d{4})-(\d{2})-(\d{2})/)
			if (!match) return null
			const year = Number(match[1])
			const month = Number(match[2])
			const day = Number(match[3])
			if (!year || !month || !day) return null
			return new Date(year, month - 1, day)
		},

		extractTime(value) {
			if (!value || typeof value !== 'string') return null
			const match = value.match(/(\d{2}):(\d{2})/)
			if (!match) return null
			return {
				hours: match[1],
				minutes: match[2],
			}
		},

		parseModelValue(val) {
			try {
				if (this.mode === 'time') {
					const time = this.extractTime(val)
					if (time) {
						this.selectedHour = Number(time.hours)
						this.selectedMinute = Number(time.minutes)
						this.hourInputValue = time.hours
						this.minuteInputValue = time.minutes
					}
					return
				}

				const date = this.extractDate(val)
				if (date) {
					this.selectedDate = new Date(
						date.getFullYear(),
						date.getMonth(),
						date.getDate()
					)
					this.currentMonth = date.getMonth()
					this.currentYear = date.getFullYear()
				}

				const time = this.extractTime(val)
				if (time) {
					this.selectedHour = Number(time.hours)
					this.selectedMinute = Number(time.minutes)
					this.hourInputValue = time.hours
					this.minuteInputValue = time.minutes
				}
			} catch {
				// Игнорируем ошибки парсинга
			}
		},

		createDayObject(date, isCurrentMonth) {
			const today = new Date()
			today.setHours(0, 0, 0, 0)

			const dateOnly = new Date(date)
			dateOnly.setHours(0, 0, 0, 0)

			const isToday = dateOnly.getTime() === today.getTime()
			const isSelected = this.selectedDate && dateOnly.getTime() === this.selectedDate.getTime()

			let isDisabled = false
			if (this.minDateObj) {
				const minDateOnly = new Date(this.minDateObj)
				minDateOnly.setHours(0, 0, 0, 0)
				isDisabled = dateOnly < minDateOnly
			}
			if (this.maxDateObj && !isDisabled) {
				const maxDateOnly = new Date(this.maxDateObj)
				maxDateOnly.setHours(23, 59, 59, 999)
				isDisabled = dateOnly > maxDateOnly
			}

			return {
				key: `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`,
				date,
				day: date.getDate(),
				isCurrentMonth,
				isToday,
				isSelected,
				isDisabled,
			}
		},

		togglePicker() {
			if (this.disabled) return
			this.isOpen = !this.isOpen

			if (this.isOpen) {
				this.showMonthSelector = false
				this.$nextTick(() => {
					this.updateDropdownPosition()
				})
			}
		},

		updateDropdownPosition() {
			const trigger = this.$refs.container
			if (!trigger) return

			const rect = trigger.getBoundingClientRect()
			const dropdownHeight = 480
			const dropdownWidth = 340
			const padding = 8

			let top = rect.bottom + padding
			let left = rect.left

			// Проверяем, помещается ли снизу
			if (top + dropdownHeight > window.innerHeight) {
				// Если не помещается снизу - открываем сверху
				const topPosition = rect.top - dropdownHeight - padding
				if (topPosition >= padding) {
					top = topPosition
				} else {
					// Если не помещается ни снизу, ни сверху - центрируем по вертикали
					top = Math.max(padding, (window.innerHeight - dropdownHeight) / 2)
				}
			}

			// Проверяем, помещается ли справа
			if (left + dropdownWidth > window.innerWidth) {
				left = window.innerWidth - dropdownWidth - padding
			}

			// Не выходим за левый край
			if (left < padding) {
				left = padding
			}

			this.dropdownStyle = {
				position: 'fixed',
				top: `${top}px`,
				left: `${left}px`,
				zIndex: 10001,
			}
		},

		handleClickOutside(e) {
			if (!this.isOpen) return

			const container = this.$refs.container
			const dropdown = this.$refs.dropdown

			if (container && !container.contains(e.target) && dropdown && !dropdown.contains(e.target)) {
				this.isOpen = false
			}
		},

		handleKeydown(e) {
			if (e.key === 'Escape' && this.isOpen) {
				this.isOpen = false
			}
		},

		prevMonth() {
			if (this.currentMonth === 0) {
				this.currentMonth = 11
				this.currentYear--
			} else {
				this.currentMonth--
			}
		},

		nextMonth() {
			if (this.currentMonth === 11) {
				this.currentMonth = 0
				this.currentYear++
			} else {
				this.currentMonth++
			}
		},

		isMonthDisabled(monthIndex) {
			if (!this.minDateObj) return false
			const checkDate = new Date(this.currentYear, monthIndex, 1)
			const minMonth = new Date(this.minDateObj.getFullYear(), this.minDateObj.getMonth(), 1)
			return checkDate < minMonth
		},

		selectMonth(index) {
			this.currentMonth = index
			this.showMonthSelector = false
		},

		selectDate(day) {
			if (day.isDisabled) return
			this.selectedDate = new Date(day.date)
			this.currentMonth = day.date.getMonth()
			this.currentYear = day.date.getFullYear()
		},

		incrementHour() {
			this.selectedHour = (this.selectedHour + 1) % 24
			this.hourInputValue = this.selectedHour.toString().padStart(2, '0')
		},

		decrementHour() {
			this.selectedHour = this.selectedHour === 0 ? 23 : this.selectedHour - 1
			this.hourInputValue = this.selectedHour.toString().padStart(2, '0')
		},

		incrementMinute() {
			this.selectedMinute = (this.selectedMinute + this.minuteStep) % 60
			this.minuteInputValue = this.selectedMinute.toString().padStart(2, '0')
		},

		decrementMinute() {
			this.selectedMinute = this.selectedMinute < this.minuteStep
				? 60 - this.minuteStep
				: this.selectedMinute - this.minuteStep
			this.minuteInputValue = this.selectedMinute.toString().padStart(2, '0')
		},

		handleHourInput(e) {
			this.isEditingHour = true
			const val = e.target.value.replace(/\D/g, '')
			this.hourInputValue = val.slice(0, 2)
		},

		handleMinuteInput(e) {
			this.isEditingMinute = true
			const val = e.target.value.replace(/\D/g, '')
			this.minuteInputValue = val.slice(0, 2)
		},

		handleHourFocus(e) {
			this.isEditingHour = true
			e.target.select()
		},

		handleMinuteFocus(e) {
			this.isEditingMinute = true
			e.target.select()
		},

		validateHour() {
			this.isEditingHour = false
			const val = this.hourInputValue.trim()
			
			if (val === '') {
				this.selectedHour = 0
				this.hourInputValue = '00'
				return
			}

			let num = parseInt(val, 10)
			if (isNaN(num) || num < 0) {
				num = 0
			} else if (num > 23) {
				num = 23
			}
			
			this.selectedHour = num
			this.hourInputValue = num.toString().padStart(2, '0')
		},

		validateMinute() {
			this.isEditingMinute = false
			const val = this.minuteInputValue.trim()
			
			if (val === '') {
				this.selectedMinute = 0
				this.minuteInputValue = '00'
				return
			}

			let num = parseInt(val, 10)
			if (isNaN(num) || num < 0) {
				num = 0
			} else if (num > 59) {
				num = 59
			}
			
			this.selectedMinute = num
			this.minuteInputValue = num.toString().padStart(2, '0')
		},

		handleHourKeydown(e) {
			if (e.key === 'Enter') {
				e.target.blur()
			} else if (e.key === 'ArrowUp') {
				e.preventDefault()
				this.isEditingHour = false
				this.incrementHour()
			} else if (e.key === 'ArrowDown') {
				e.preventDefault()
				this.isEditingHour = false
				this.decrementHour()
			}
		},

		handleMinuteKeydown(e) {
			if (e.key === 'Enter') {
				e.target.blur()
			} else if (e.key === 'ArrowUp') {
				e.preventDefault()
				this.isEditingMinute = false
				this.incrementMinute()
			} else if (e.key === 'ArrowDown') {
				e.preventDefault()
				this.isEditingMinute = false
				this.decrementMinute()
			}
		},

		isQuickTimeSelected(time) {
			return this.selectedHour === time.hour && this.selectedMinute === time.minute
		},

		selectQuickTime(time) {
			this.selectedHour = time.hour
			this.selectedMinute = time.minute
			this.hourInputValue = time.hour.toString().padStart(2, '0')
			this.minuteInputValue = time.minute.toString().padStart(2, '0')
		},

		selectToday() {
			const now = new Date()
			if (this.mode !== 'time') {
				this.selectedDate = new Date(
					now.getFullYear(),
					now.getMonth(),
					now.getDate()
				)
				this.currentMonth = now.getMonth()
				this.currentYear = now.getFullYear()
			}

			const nextHour = now.getHours() + 1
			this.selectedHour = nextHour > 23 ? 23 : nextHour
			this.selectedMinute = 0

			this.hourInputValue = this.selectedHour.toString().padStart(2, '0')
			this.minuteInputValue = '00'
		},

		clearSelection() {
			this.selectedDate = null
			this.selectedHour = 12
			this.selectedMinute = 0
			this.hourInputValue = '12'
			this.minuteInputValue = '00'
			this.$emit('update:modelValue', '')
			this.$emit('change', '')
			this.isOpen = false
		},

		applySelection() {
			if (this.mode === 'time') {
				const hours = String(this.selectedHour).padStart(2, '0')
				const minutes = String(this.selectedMinute).padStart(2, '0')
				const value = `${hours}:${minutes}`
				this.$emit('update:modelValue', value)
				this.$emit('change', value)
				this.isOpen = false
				return
			}

			if (!this.selectedDate) {
				this.selectToday()
			}

			const result = new Date(
				this.selectedDate.getFullYear(),
				this.selectedDate.getMonth(),
				this.selectedDate.getDate(),
				this.selectedHour,
				this.selectedMinute,
				0,
				0
			)

			const year = result.getFullYear()
			const month = String(result.getMonth() + 1).padStart(2, '0')
			const day = String(result.getDate()).padStart(2, '0')

			if (this.mode === 'date') {
				const value = `${year}-${month}-${day}`
				this.$emit('update:modelValue', value)
				this.$emit('change', value)
				this.isOpen = false
				return
			}

			const hours = String(result.getHours()).padStart(2, '0')
			const minutes = String(result.getMinutes()).padStart(2, '0')

			const value = `${year}-${month}-${day}T${hours}:${minutes}`

			this.$emit('update:modelValue', value)
			this.$emit('change', value)
			this.isOpen = false
		},
	},
}
</script>


<style scoped>
.datetime-picker {
	position: relative;
	width: 100%;
}

.datetime-picker__trigger {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: #fff;
	border: 2px solid #e8e8e8;
	border-radius: 10px;
	cursor: pointer;
	transition: all 0.2s ease;
	min-height: 48px;
}

.datetime-picker__trigger:hover:not(.datetime-picker__trigger--disabled) {
	border-color: #c4c4c4;
}

.datetime-picker__trigger--focused {
	border-color: #0095ec;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.12);
}

.datetime-picker__trigger--error {
	border-color: #dc3545;
}

.datetime-picker__trigger--error:focus,
.datetime-picker__trigger--error.datetime-picker__trigger--focused {
	box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.12);
}

.datetime-picker__trigger--disabled {
	background: #f5f5f5;
	cursor: not-allowed;
	opacity: 0.7;
}

.datetime-picker__icon {
	color: #636363;
	display: flex;
	flex-shrink: 0;
}

.datetime-picker__trigger--focused .datetime-picker__icon {
	color: #0095ec;
}

.datetime-picker__value {
	flex: 1;
	min-width: 0;
}

.datetime-picker__text {
	font-family: 'Poppins', sans-serif;
	font-size: 14px;
	font-weight: 500;
	color: #252641;
}
.datetime-picker__placeholder {
font-family: 'Poppins', sans-serif;
font-size: 14px;
color: #9a9a9a;
}
.datetime-picker__arrow {
color: #636363;
display: flex;
transition: transform 0.2s ease;
}
.datetime-picker__arrow--open {
transform: rotate(180deg);
}
/* Dropdown */
.datetime-picker__dropdown {
background: #fff;
border-radius: 16px;
box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1);
width: 340px;
overflow: hidden;
}
.datetime-picker__content {
padding: 20px;
}
/* Calendar */
.datetime-picker__calendar {
margin-bottom: 16px;
}
.datetime-picker__calendar-header {
display: flex;
align-items: center;
justify-content: space-between;
margin-bottom: 16px;
}
.datetime-picker__nav-btn {
width: 36px;
height: 36px;
display: flex;
align-items: center;
justify-content: center;
background: transparent;
border: none;
border-radius: 8px;
color: #636363;
cursor: pointer;
transition: all 0.15s ease;
}
.datetime-picker__nav-btn:hover:not(:disabled) {
background: #f0f0f0;
color: #252641;
}
.datetime-picker__nav-btn:disabled {
opacity: 0.3;
cursor: not-allowed;
}
.datetime-picker__month-year {
flex: 1;
text-align: center;
}
.datetime-picker__month-btn {
display: inline-flex;
align-items: center;
gap: 6px;
padding: 8px 12px;
background: transparent;
border: none;
border-radius: 8px;
font-family: 'Poppins', sans-serif;
font-size: 15px;
font-weight: 600;
color: #252641;
cursor: pointer;
transition: background 0.15s ease;
}
.datetime-picker__month-btn:hover {
background: #f0f0f0;
}
/* Month selector */
.datetime-picker__month-selector {
position: absolute;
top: 60px;
left: 20px;
right: 20px;
background: #fff;
border-radius: 12px;
box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
padding: 16px;
z-index: 10;
}
.datetime-picker__year-nav {
display: flex;
align-items: center;
justify-content: center;
gap: 16px;
margin-bottom: 12px;
}
.datetime-picker__year-btn {
width: 32px;
height: 32px;
display: flex;
align-items: center;
justify-content: center;
background: #f5f5f5;
border: none;
border-radius: 8px;
color: #636363;
cursor: pointer;
transition: all 0.15s ease;
}
.datetime-picker__year-btn:hover {
background: #e8e8e8;
color: #252641;
}
.datetime-picker__year-label {
font-family: 'Poppins', sans-serif;
font-size: 16px;
font-weight: 600;
color: #252641;
min-width: 60px;
text-align: center;
}
.datetime-picker__months-grid {
display: grid;
grid-template-columns: repeat(4, 1fr);
gap: 8px;
}
.datetime-picker__month-item {
padding: 10px 8px;
background: transparent;
border: none;
border-radius: 8px;
font-family: 'Poppins', sans-serif;
font-size: 13px;
color: #636363;
cursor: pointer;
transition: all 0.15s ease;
}
.datetime-picker__month-item:hover:not(:disabled) {
background: #f0f0f0;
color: #252641;
}
.datetime-picker__month-item--selected {
background: #0095ec;
color: #fff;
}
.datetime-picker__month-item--selected:hover {
background: #0077be;
color: #fff;
}
.datetime-picker__month-item--disabled {
opacity: 0.3;
cursor: not-allowed;
}
/* Weekdays */
.datetime-picker__weekdays {
display: grid;
grid-template-columns: repeat(7, 1fr);
gap: 4px;
margin-bottom: 8px;
}
.datetime-picker__weekday {
text-align: center;
font-family: 'Poppins', sans-serif;
font-size: 12px;
font-weight: 600;
color: #9a9a9a;
padding: 4px;
}
/* Days */
.datetime-picker__days {
display: grid;
grid-template-columns: repeat(7, 1fr);
gap: 4px;
}
.datetime-picker__day {
aspect-ratio: 1;
display: flex;
align-items: center;
justify-content: center;
background: transparent;
border: none;
border-radius: 8px;
font-family: 'Poppins', sans-serif;
font-size: 13px;
font-weight: 500;
color: #252641;
cursor: pointer;
transition: all 0.15s ease;
}
.datetime-picker__day:hover:not(:disabled):not(.datetime-picker__day--selected) {
background: #f0f0f0;
}
.datetime-picker__day--other-month {
color: #c4c4c4;
}
.datetime-picker__day--today {
background: #e3f2fd;
color: #0095ec;
font-weight: 600;
}
.datetime-picker__day--selected {
background: #0095ec;
color: #fff;
}
.datetime-picker__day--selected:hover {
background: #0077be;
}
.datetime-picker__day--disabled {
opacity: 0.3;
cursor: not-allowed;
}
/* Time */
.datetime-picker__time {
padding-top: 16px;
border-top: 1px solid #e8e8e8;
}
.datetime-picker__time-header {
display: flex;
align-items: center;
gap: 8px;
margin-bottom: 12px;
color: #636363;
font-family: 'Poppins', sans-serif;
font-size: 13px;
font-weight: 500;
}
.datetime-picker__time-selectors {
display: flex;
align-items: center;
justify-content: center;
gap: 8px;
margin-bottom: 12px;
}
.datetime-picker__time-column {
display: flex;
flex-direction: column;
align-items: center;
gap: 4px;
}
.datetime-picker__time-btn {
width: 40px;
height: 28px;
display: flex;
align-items: center;
justify-content: center;
background: #f5f5f5;
border: none;
border-radius: 6px;
color: #636363;
cursor: pointer;
transition: all 0.15s ease;
}
.datetime-picker__time-btn:hover {
background: #e8e8e8;
color: #252641;
}
.datetime-picker__time-btn--up {
border-radius: 6px 6px 2px 2px;
}
.datetime-picker__time-btn--down {
border-radius: 2px 2px 6px 6px;
}
.datetime-picker__time-input {
width: 56px;
height: 44px;
text-align: center;
font-family: 'Poppins', sans-serif;
font-size: 20px;
font-weight: 600;
color: #252641;
background: #f8f9fa;
border: 2px solid #e8e8e8;
border-radius: 8px;
outline: none;
transition: all 0.15s ease;
}
.datetime-picker__time-input:focus {
border-color: #0095ec;
background: #fff;
}
.datetime-picker__time-separator {
font-family: 'Poppins', sans-serif;
font-size: 24px;
font-weight: 600;
color: #636363;
padding: 0 4px;
}
/* Quick times */
.datetime-picker__quick-times {
display: flex;
flex-wrap: wrap;
gap: 8px;
justify-content: center;
}
.datetime-picker__quick-time {
padding: 6px 12px;
background: #f5f5f5;
border: none;
border-radius: 16px;
font-family: 'Poppins', sans-serif;
font-size: 12px;
font-weight: 500;
color: #636363;
cursor: pointer;
transition: all 0.15s ease;
}
.datetime-picker__quick-time:hover {
background: #e8e8e8;
color: #252641;
}
.datetime-picker__quick-time--selected {
background: #0095ec;
color: #fff;
}
.datetime-picker__quick-time--selected:hover {
background: #0077be;
}
/* Footer */
.datetime-picker__footer {
display: flex;
gap: 8px;
padding: 16px 20px;
background: #f8f9fa;
border-top: 1px solid #e8e8e8;
}
.datetime-picker__footer-btn {
flex: 1;
padding: 10px 16px;
border: none;
border-radius: 8px;
font-family: 'Poppins', sans-serif;
font-size: 13px;
font-weight: 500;
cursor: pointer;
transition: all 0.15s ease;
}
.datetime-picker__footer-btn--clear {
background: transparent;
color: #dc3545;
}
.datetime-picker__footer-btn--clear:hover {
background: rgba(220, 53, 69, 0.1);
}
.datetime-picker__footer-btn--today {
background: #f0f0f0;
color: #636363;
}
.datetime-picker__footer-btn--today:hover {
background: #e8e8e8;
color: #252641;
}
.datetime-picker__footer-btn--apply {
background: #0095ec;
color: #fff;
}
.datetime-picker__footer-btn--apply:hover {
background: #0077be;
}
/* Transitions */
.dropdown-enter-active,
.dropdown-leave-active {
transition: all 0.2s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
opacity: 0;
transform: translateY(-8px);
}
.fade-enter-active,
.fade-leave-active {
transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
opacity: 0;
}
</style>
